<script setup lang="ts">
/**
 * „Neues Passwort" and „Neues Passwort wiederholen": the pair every flow that
 * sets a password asks for (the required change at sign-in, a password
 * change). It checks what master checks — at least `minLength` characters,
 * not the current password, both entries equal — and says what is wrong
 * under the field it concerns. A message appears once its field has been
 * left, or right away with `showErrors` (set it after a submit attempt).
 *
 * The caller gates its submit with `newPasswordProblem()`, the same check.
 * nx-ui carries no i18n: pass localized copy for the German defaults.
 */
import { computed, ref, useId } from 'vue';
import type { InputHeight } from './internal';
import NxLabel from './NxLabel.vue';
import NxPasswordInput from './NxPasswordInput.vue';
import { NEW_PASSWORD_MIN_LENGTH, newPasswordProblem } from './newPassword';

const props = withDefaults(
  defineProps<{
    /** Id of the first input; the repetition gets `<id>-repeat`. Generated when omitted. */
    id?: string;
    label?: string;
    repeatLabel?: string;
    minLength?: number;
    /** When given, a new password equal to it is refused. */
    currentPassword?: string;
    /** Shows every message now instead of after the field was left. */
    showErrors?: boolean;
    disabled?: boolean;
    height?: InputHeight;
    /** Default: „Mindestens {minLength} Zeichen." */
    tooShortMessage?: string;
    mismatchMessage?: string;
    unchangedMessage?: string;
  }>(),
  {
    id: undefined,
    label: 'Neues Passwort',
    repeatLabel: 'Neues Passwort wiederholen',
    minLength: NEW_PASSWORD_MIN_LENGTH,
    currentPassword: undefined,
    showErrors: false,
    disabled: false,
    height: 's',
    tooShortMessage: undefined,
    mismatchMessage: 'Die Passwörter stimmen nicht überein.',
    unchangedMessage: 'Das neue Passwort muss sich vom bisherigen unterscheiden.',
  }
);

const model = defineModel<string>({ required: true });
const repeat = defineModel<string>('repeat', { default: '' });

const generatedId = useId();
const baseId = computed(() => props.id ?? generatedId);
const repeatId = computed(() => `${baseId.value}-repeat`);
const messageId = computed(() => `${baseId.value}-message`);
const repeatMessageId = computed(() => `${baseId.value}-repeat-message`);

const newLeft = ref(false);
const repeatLeft = ref(false);

const problem = computed(() =>
  newPasswordProblem(model.value, repeat.value, {
    minLength: props.minLength,
    currentPassword: props.currentPassword,
  })
);

const newMessage = computed(() => {
  if (!props.showErrors && !newLeft.value) return '';
  if (problem.value === 'too_short') {
    return props.tooShortMessage ?? `Mindestens ${props.minLength} Zeichen.`;
  }
  if (problem.value === 'unchanged') return props.unchangedMessage;
  return '';
});

const repeatMessage = computed(() => {
  if (!props.showErrors && !repeatLeft.value) return '';
  return problem.value === 'mismatch' ? props.mismatchMessage : '';
});
</script>

<template>
  <div class="nx-new-password-field">
    <NxLabel :text="props.label" :for="baseId">
      <NxPasswordInput
        :id="baseId"
        v-model="model"
        :height="props.height"
        :disabled="props.disabled"
        autocomplete="new-password"
        :aria-invalid="newMessage ? 'true' : undefined"
        :aria-describedby="newMessage ? messageId : undefined"
        @blur="newLeft = true"
      />
      <p v-if="newMessage" :id="messageId" class="nx-new-password-field-message" aria-live="polite">
        {{ newMessage }}
      </p>
    </NxLabel>
    <NxLabel :text="props.repeatLabel" :for="repeatId">
      <NxPasswordInput
        :id="repeatId"
        v-model="repeat"
        :height="props.height"
        :disabled="props.disabled"
        autocomplete="new-password"
        :aria-invalid="repeatMessage ? 'true' : undefined"
        :aria-describedby="repeatMessage ? repeatMessageId : undefined"
        @blur="repeatLeft = true"
      />
      <p
        v-if="repeatMessage"
        :id="repeatMessageId"
        class="nx-new-password-field-message"
        aria-live="polite"
      >
        {{ repeatMessage }}
      </p>
    </NxLabel>
  </div>
</template>

<style scoped>
.nx-new-password-field {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
  width: 100%;
}

.nx-new-password-field-message {
  margin: 0;
  /* The global type reset sizes every bare <p> at 16px medium. */
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  color: var(--nxt-danger-subtle-fg);
}
</style>
